import React, { useState } from "react";
import { Box, Typography, Button, Collapse, CircularProgress, Tooltip } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import moment from "moment";
import {
  bg, border, textPrimary, textSecondary, accent, font,
  success, warning, error as errorColor, avatarColorFor,
} from "../../../layouts/schoolPortal/schoolPortalTheme";
import { label as labelSx, meta as metaSx } from "./recordTheme";

/**
 * The header band. Reads like a deal stage, because that is what it is: who,
 * where in the pipeline, what is wrong, what they are worth, and the actions.
 *
 * A CFI three minutes before a session reads down and stops when they have
 * enough — so the cheap verdict is at the top and the expensive history is not.
 */

// The risk band and the money formatter both lived here and are gone with the
// stats they served. Risk is a roster question the work queue already answers;
// on one student's record it labelled a brand-new student "Watch" for not
// having opened a lesson the instructor had not yet invited them to.

function Stat({ label, children, tip }) {
  const body = (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ ...labelSx, fontFamily: font }}>{label}</Typography>
      <Box sx={{ mt: 0.25 }}>{children}</Box>
    </Box>
  );
  return tip ? <Tooltip title={tip} arrow>{body}</Tooltip> : body;
}

export default function RecordHeader({
  record, aiSummary, aiLoading, onGenerateAI, actions, showBriefing = true,
}) {
  const [aiOpen, setAiOpen] = useState(false);
  const { student, pipeline, progress, readiness, health, modules = {}, course } = record;
  // The server says what this student's course calls things — a UK PPL
  // "lesson" is one of nine CAA exam subjects, and labelling it as a lesson is
  // wrong in the one place an instructor is looking. Defaults keep older
  // payloads, which carry no `course`, rendering exactly as before.
  const lessonWord = course?.lessonWord || "lesson";
  const examLabel = course?.examLabel || "Written readiness";
  const practiceTotals = record.coverage?.totals || { done: 0, right: 0 };
  // Same rule as the roster: colour is health, length is completion.
  const barColour = health === "serious" ? errorColor : health === "concern" ? warning : success;
  const initials = (student.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  // Ground truth outranks opinion. A real written result REPLACES the
  // prediction, and the frozen prediction sits beside it so calibration is
  // visible rather than quietly overwritten.
  const written = readiness?.source === "faa_written";
  // Lesson-mode progress exists at all. Practice questions never write to
  // AiLearnProgress, so this is false for most students and the band that
  // depends on it should not be drawn.
  const hasProgress = Boolean(progress && progress.completion > 0);

  return (
    // CHROME, NOT A CARD.
    //
    // Who the student is, and what you can do to them, is the frame around the
    // page rather than an object on it. As a white card it was one of six,
    // sitting first, which made the eye start on the least actionable thing
    // there. On the page's own ground with a rule under it, the first white
    // block a reader meets is the brief — which is the one to read first.
    //
    // Still opaque: it is sticky, and content scrolling under a transparent
    // header shows through it.
    <Box sx={{
      position: "sticky", top: 0, zIndex: 5, backgroundColor: bg,
      borderBottom: `1px solid ${border}`,
      padding: "12px 2px 12px", marginBottom: "16px",
    }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flexWrap: "wrap" }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: "10px", flexShrink: 0,
          backgroundColor: avatarColorFor ? avatarColorFor(student.name || student.email) : accent,
          color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: font, fontWeight: 700, fontSize: "14px",
        }}>{initials}</Box>

        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography sx={{ fontFamily: font, fontSize: "18px", fontWeight: 800, color: textPrimary, letterSpacing: "-0.2px" }}>
            {student.name}
          </Typography>
          <Typography sx={{ ...metaSx, fontFamily: font }}>
            {student.email}
            {/* "Worked" and "signed in" are different claims and the record
                now knows which this is. The old page said "last active" off
                lastLogin, so it could report someone active two days ago while
                the panel beside it said nothing done in ten — opening the app
                and going to bed counted as activity. */}
            {student.lastActive
              ? ` · last ${student.lastActiveIsWork ? "worked" : "signed in"} ${moment(student.lastActive).fromNow()}`
              : " · never signed in"}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {actions}
        </Box>
      </Box>

      {/* The stage line — drawn only when it has something in it.
          Gating Course progress and Exam readiness on real data left this as a
          bordered band of whitespace whose one sentence pointed further down
          the page. An empty frame is not neutral: it reads as a thing that
          failed to load. */}
      {(hasProgress || written) && (
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "1.6fr 1fr 1fr 1.1fr" },
        gap: 2, mt: 1.5, pt: 1.25, borderTop: `1px solid ${border}`,
      }}>
        {/* THIS NUMBER WAS LYING, IN GREEN, AT THE TOP OF THE PAGE.
            `progress` comes from AiLearnProgress — lesson-mode rows — which
            practice questions never write to. A student with 229 answered
            questions at 59% right had exactly one such row, so the first thing
            an instructor read was "0% covered · 1% scoring" while the table
            below said "229 questions · 59% right". The header's own design is
            that you read down and stop when you have enough, which made this
            the worst possible place for it.
            Shown only when the lesson data actually says something; otherwise
            the question counts speak for themselves further down. */}
        <Stat label="Course progress">
          {hasProgress ? (
            <>
              {/* How much, and how well. "Lesson 7 of 16" implied a position in
                  a queue, and the syllabus is advisory — a student can do
                  airspace before weight and balance, and the number went
                  backwards when they worked ahead. The lesson checklist in the
                  left rail is still the honest per-lesson view. */}
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
                <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: textPrimary, fontVariantNumeric: "tabular-nums" }}>
                  {progress.completion}% of lessons done
                </Typography>
              </Box>
              <Box sx={{ height: 4, borderRadius: 2, backgroundColor: "#F0EDE9", mt: 0.5, mb: 0.4, overflow: "hidden", maxWidth: 220 }}>
                <Box sx={{ width: `${progress.completion}%`, height: "100%", backgroundColor: barColour }} />
              </Box>
              <Typography sx={{ ...metaSx, fontFamily: font }}>
                {progress.lessonsMet} of {progress.lessonsTotal} {lessonWord}s finished
                {pipeline?.lessonTitle ? ` · on ${pipeline.lessonTitle}` : ""}
              </Typography>
            </>
          ) : modules.learn === false ? (
            <Typography sx={{ ...metaSx, fontFamily: font }}>Learn module not enabled.</Typography>
          ) : (
            <Typography sx={{ ...metaSx, fontFamily: font }}>
              No lesson-mode work yet — see the question counts below.
            </Typography>
          )}
        </Stat>


        {/* A prediction of failure on a student with no data is not a
            prediction, it is a default — "Not ready · 0% of program" was the
            page telling an instructor their brand-new student will fail. Shown
            once there is a real result, or once they have actually done
            something to estimate from. */}
        {(written || (progress?.completion || 0) > 0) && (
        <Stat label={written ? "Written result" : "Practice"}>
          {!readiness ? (
            <Typography sx={{ ...metaSx, fontFamily: font }}>Insights module not enabled.</Typography>
          ) : written ? (
            <>
              <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: readiness.passed ? success : errorColor }}>
                {readiness.score}% {readiness.passed ? "pass" : "fail"}
              </Typography>
              <Typography sx={{ ...metaSx, fontFamily: font }}>
                {moment(readiness.takenAt).format("D MMM YYYY")}
                {readiness.wePredicted != null && ` · we predicted ${readiness.wePredicted ? "ready" : "not ready"}`}
              </Typography>
            </>
          ) : (
            <>
              {/* What they have actually answered, in plain numbers. "0% of the
                  way" was a share of finished subjects that read as a verdict;
                  the per-paper verdict is the Exam picture below. */}
              <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: readiness?.readyForWritten ? success : textPrimary, fontVariantNumeric: "tabular-nums" }}>
                {practiceTotals.done ? `${practiceTotals.done} questions · ${Math.round((practiceTotals.right / practiceTotals.done) * 100)}% right` : "No practice yet"}
              </Typography>
              <Typography sx={{ ...metaSx, fontFamily: font }}>
                {readiness?.readyForWritten ? "Ready for the exam on our estimate" : "on what they have practised so far"}
              </Typography>
            </>
          )}
        </Stat>
        )}

        {/* Revenue per student lived here and has moved to the earnings page.
            An instructor opening a record five minutes before a lesson does
            not need to be told what this person is worth, and putting it in
            the header of a teaching page is what made the tool feel like a
            CRM. */}
      </Box>
      )}

      {/* AI verdict — one line that expands into its evidence. A generated
          briefing is a view on the record, not the record. */}
      {/* Hidden where "What to do" leads the page — it says the same thing,
          with evidence and buttons, without a click to generate it. */}
      <Box sx={{ mt: 1.25, pt: 1, borderTop: `1px solid ${border}`, display: showBriefing ? "flex" : "none", alignItems: "center", gap: 1 }}>
        <AutoAwesomeIcon sx={{ fontSize: 14, color: accent, flexShrink: 0 }} />
        {aiLoading ? (
          <CircularProgress size={13} sx={{ color: accent }} />
        ) : aiSummary ? (
          <>
            <Typography
              onClick={() => setAiOpen((o) => !o)}
              sx={{
                fontFamily: font, fontSize: "12.5px", color: textPrimary, cursor: "pointer",
                flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {aiSummary.verdict || aiSummary.headline || "Briefing available"}
            </Typography>
            <Typography onClick={() => setAiOpen((o) => !o)} sx={{ ...metaSx, fontFamily: font, cursor: "pointer", flexShrink: 0 }}>
              {aiOpen ? "hide" : "evidence"}
            </Typography>
          </>
        ) : (
          <Button onClick={() => onGenerateAI(false)} size="small" sx={{
            textTransform: "none", fontFamily: font, fontSize: "12px", fontWeight: 600, color: accent, p: 0, minWidth: 0,
          }}>
            Generate a briefing on this student
          </Button>
        )}
      </Box>

      <Collapse in={aiOpen} unmountOnExit>
        <Box sx={{ mt: 1, p: 1.25, backgroundColor: "#F5F2EF", borderRadius: "8px" }}>
          {aiSummary?.action && (
            <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 700, color: textPrimary, mb: 0.75 }}>
              Do this: {aiSummary.action}
            </Typography>
          )}
          {(aiSummary?.patterns || []).map((p, i) => (
            <Box key={i} sx={{ mb: 0.75 }}>
              <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 600, color: textPrimary }}>
                {p.title || p.pattern || p.name}
              </Typography>
              {(p.evidence || p.detail || p.description) && (
                <Typography sx={{ ...metaSx, fontFamily: font, lineHeight: 1.45 }}>
                  {p.evidence || p.detail || p.description}
                </Typography>
              )}
            </Box>
          ))}
          <Button onClick={() => onGenerateAI(true)} size="small" sx={{
            textTransform: "none", fontFamily: font, fontSize: "11.5px", color: textSecondary, p: 0, minWidth: 0, mt: 0.5,
          }}>
            Regenerate
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
}
